import { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import SectionBadge from "@/components/landing/SectionBadge";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { api, formatDate, courseImageUrl, assetUrl, type Course, type Diplomado, type NewsPost, type Partner } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";
import { LANDING_IMAGES } from "@/lib/landingImages";
import {
  LANDING_STATS,
  LANDING_SERVICES,
  LANDING_ABOUT_CHECKS,
  LANDING_IMPACT,
  LANDING_SPECIALIZATIONS,
  LANDING_TESTIMONIALS,
  LANDING_CLIENTS,
  DEFAULT_NEWS,
  courseIcon,
} from "@/lib/landingContent";

function SectionReveal({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function ScrollHint() {
  return (
    <div className="flex flex-col items-center gap-2 text-white/50 text-xs mt-12">
      <i className="ri-mouse-line text-lg animate-bounce"></i>
      <span>Desplázate para descubrir</span>
    </div>
  );
}

function formatShortDate(d: string) {
  return formatDate(d);
}

export default function HomePage() {
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [contactType, setContactType] = useState<"b2b" | "b2c">("b2b");
  const [contactStatus, setContactStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [contactError, setContactError] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [diplomados, setDiplomados] = useState<Diplomado[]>([]);
  const [news, setNews] = useState<NewsPost[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);

  const heroImage = branding.hero_home || LANDING_IMAGES.hero;
  const contactEmail = branding.contact_email || "cursos@kasumar.com";

  useEffect(() => {
    api.getPublicCourses().then(setCourses).catch(() => setCourses([]));
    api.getPublicDiplomados().then(setDiplomados).catch(() => setDiplomados([]));
    api.getPublicNews().then(setNews).catch(() => setNews([]));
    api.getPublicPartners().then(setPartners).catch(() => setPartners([]));
  }, [location.pathname]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    
    const name = fd.get("nombre") as string;
    const email = fd.get("email") as string;
    const phone = (fd.get("telefono") as string) || "";
    const message = ((fd.get("mensaje") as string) || "").trim();

    setContactError("");
    setContactStatus("submitting");

    try {
      if (contactType === "b2c") {
        // Guardar también en bandeja de contactos (antes solo redirigía a registro)
        await api.submitContact({
          type: "b2c",
          name,
          email,
          phone,
          message: message || "Solicitud de registro desde el formulario de contacto (participante independiente).",
        });
        navigate(`/registro?nombre=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&telefono=${encodeURIComponent(phone)}`);
        return;
      }

      await api.submitContact({
        type: "b2b",
        name,
        email,
        phone: "",
        company: (fd.get("empresa") as string) || "",
        position: (fd.get("cargo") as string) || "",
        message: message || "Consulta empresarial desde el formulario de contacto.",
      });
      setContactStatus("success");
      form.reset();
    } catch {
      setContactError("Error al enviar. Intente nuevamente.");
      setContactStatus("error");
    }
  };

  const nextEdition = (c: Course) => c.editions?.[0];
  const nextDiplomadoEdition = (d: Diplomado) => d.editions?.[0];

  const stats = useMemo(
    () =>
      LANDING_STATS.map((s) =>
        "dynamicCourses" in s && s.dynamicCourses
          ? { ...s, value: courses.length > 0 ? String(courses.length) : "16" }
          : s
      ),
    [courses.length]
  );

  const displayNews: Array<{ id?: string; title: string; category: string; published_at: string; image_url: string }> =
    news.length > 0 ? news.slice(0, 4) : DEFAULT_NEWS;

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <PageMeta path="/" />
      <PublicNavbar />

      {/* HERO */}
      <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080f1a]/70 via-[#080f1a]/50 to-[#080f1a]/90" />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 pt-[72px] text-center py-28">
          <SectionReveal>
            <div className="flex flex-col items-center mb-8">
              <img
                src={branding.logo_full}
                alt={branding.site_name || "KSM Services"}
                className="h-16 sm:h-20 md:h-24 w-auto mb-6 drop-shadow-[0_0_24px_rgba(255,255,255,0.35)] brightness-0 invert"
              />
              <SectionBadge icon="ri-shield-check-line" label="Capacitación industrial certificada" />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
              {branding.hero_title || "Formamos al talento"}
              <br />
              <span className="text-[var(--color-accent-oil)]">
                {branding.hero_title_accent || "que mueve la industria"}
              </span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-10 leading-relaxed whitespace-pre-line">
              {branding.hero_description ||
                "Programas de formación certificados en seguridad industrial, salud ocupacional y medio ambiente. Respaldados por 15 años de experiencia en el sector Oil & Gas mexicano."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/registro"
                className="inline-flex items-center justify-center gap-2 bg-[var(--color-accent-oil)] text-[#080f1a] px-10 py-4 rounded-2xl font-bold hover:brightness-110 transition-all shadow-lg shadow-black/30"
              >
                <i className="ri-user-add-line"></i>
                Registrarse Ahora
              </Link>
              <a
                href="#cursos"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-10 py-4 rounded-2xl font-semibold hover:bg-white/10 transition-all"
              >
                <i className="ri-book-open-line"></i>
                Ver Cursos
              </a>
            </div>
            <ScrollHint />
          </SectionReveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-[var(--color-bg-primary)]">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 px-4">
          {stats.map((s, i) => (
            <SectionReveal key={s.label} delay={i * 80}>
              <div className="border border-[var(--color-border)] rounded-2xl p-6 text-center hover:border-[var(--color-accent-oil)]/40 transition-all">
                <div className="w-12 h-12 rounded-xl border border-[var(--color-accent-oil)]/50 flex items-center justify-center mx-auto mb-4">
                  <i className={`${s.icon} text-xl text-[var(--color-accent-oil)]`}></i>
                </div>
                <p className="text-3xl md:text-4xl font-bold text-white">{s.value}</p>
                <p className="text-sm text-[var(--color-text-secondary)] mt-2">{s.label}</p>
              </div>
            </SectionReveal>
          ))}
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="py-20 bg-[var(--color-bg-secondary)]">
        <div className="max-w-6xl mx-auto px-4">
          <SectionReveal>
            <div className="text-center mb-14">
              <div className="flex justify-center mb-4">
                <SectionBadge icon="ri-briefcase-4-line" label="Soluciones integrales" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Servicios que impulsan tu operación</h2>
              <p className="text-[var(--color-text-secondary)] max-w-2xl mx-auto">
                Tres pilares fundamentales para el desarrollo del talento humano en la industria energética.
              </p>
            </div>
          </SectionReveal>
          <div className="grid md:grid-cols-3 gap-6">
            {LANDING_SERVICES.map((svc, i) => (
              <SectionReveal key={svc.title} delay={i * 100}>
                <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-8 h-full hover:border-[var(--color-accent-oil)]/30 transition-all">
                  <div className="w-12 h-12 rounded-xl border border-[var(--color-accent-oil)]/50 flex items-center justify-center mb-5">
                    <i className={`${svc.icon} text-xl text-[var(--color-accent-oil)]`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{svc.title}</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed mb-5">{svc.description}</p>
                  <ul className="space-y-2">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                        <i className="ri-check-line text-[var(--color-accent-oil)]"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUIÉNES SOMOS */}
      <section id="nosotros" className="py-20 bg-[var(--color-bg-primary)]">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <SectionReveal>
            <div className="mb-4">
              <SectionBadge icon="ri-information-line" label="Quiénes somos" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Excelencia en
              <br />
              <span className="text-[var(--color-accent-oil)]">Capacitación Industrial</span>
            </h2>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
              Somos una empresa dedicada a brindar servicios de capacitación en Talento Humano en las áreas de formación:
              Desarrollo Personal, Informática, Seguridad, Ingeniería y Geociencias.
            </p>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-6">
              Asimismo ofrecemos consultorías, desarrollo de proyectos y soluciones asociadas a operaciones en el área
              industrial, petrolera y suministros.
            </p>
            <ul className="space-y-3">
              {LANDING_ABOUT_CHECKS.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[var(--color-text-secondary)]">
                  <i className="ri-check-double-line text-[var(--color-accent-oil)] mt-0.5 shrink-0"></i>
                  {item}
                </li>
              ))}
            </ul>
          </SectionReveal>
          <SectionReveal delay={150}>
            <div className="relative rounded-2xl overflow-hidden border border-[var(--color-border)]">
              <img src={branding.landing_about || LANDING_IMAGES.about} alt="Capacitación industrial KSM" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-[#080f1a]/80 backdrop-blur-sm px-5 py-4 flex items-center gap-3">
                <i className="ri-award-line text-2xl text-[var(--color-accent-oil)]"></i>
                <div>
                  <p className="font-bold text-white">ISO 9001:2015</p>
                  <p className="text-xs text-white/60">Certificación de calidad internacional</p>
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* CURSOS */}
      <section id="cursos" className="py-20 bg-[var(--color-bg-secondary)]">
        <div className="max-w-6xl mx-auto px-4">
          <SectionReveal>
            <div className="text-center mb-14">
              <div className="flex justify-center mb-4">
                <SectionBadge icon="ri-book-open-line" label="Catálogo de cursos" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Cursos disponibles para tu equipo</h2>
              <p className="text-[var(--color-text-secondary)] max-w-2xl mx-auto">
                Programas certificados impartidos por instructores con experiencia internacional en la industria.
              </p>
            </div>
          </SectionReveal>
          {courses.length === 0 ? (
            <p className="text-center text-[var(--color-text-muted)]">
              No hay cursos publicados en este momento. Contáctenos para próximas ediciones.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course, i) => {
                const ed = nextEdition(course);
                const icon = courseIcon(course.name, course.category);
                return (
                  <SectionReveal key={course.id} delay={i * 60}>
                    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-6 h-full flex flex-col hover:border-[var(--color-accent-oil)]/30 transition-all">
                      <div className="flex items-start justify-between mb-4">
                        {course.image_url ? (
                          <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-3 bg-[var(--color-bg-secondary)]">
                            <img src={courseImageUrl(course.image_url, branding.course_default)} alt={course.name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-11 h-11 rounded-xl border border-[var(--color-accent-oil)]/50 flex items-center justify-center">
                            <i className={`${icon} text-lg text-[var(--color-accent-oil)]`}></i>
                          </div>
                        )}
                        {!course.image_url && (
                          <span className="px-3 py-1 rounded-full text-sm font-bold text-[var(--color-accent-oil)] border border-[var(--color-accent-oil)]/40 h-fit">
                            ${(ed?.price || 0).toLocaleString("en-US")}
                          </span>
                        )}
                      </div>
                      {course.image_url && (
                        <div className="flex justify-end mb-2">
                          <span className="px-3 py-1 rounded-full text-sm font-bold text-[var(--color-accent-oil)] border border-[var(--color-accent-oil)]/40 inline-block">
                            ${(ed?.price || 0).toLocaleString("en-US")}
                          </span>
                        </div>
                      )}
                      <h3 className="font-bold text-lg mb-2 line-clamp-2">{course.name}</h3>
                      <p className="text-sm text-[var(--color-text-secondary)] line-clamp-3 flex-grow mb-4">{course.description}</p>
                      {ed && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-text-muted)] mb-5">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {ed.start_date.slice(0, 10)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {course.hours}h
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {ed.location}
                          </span>
                        </div>
                      )}
                      <Link
                        to={`/curso/${course.id}`}
                        className="block w-full text-center py-3 rounded-xl border border-[var(--color-border)] text-sm font-semibold hover:border-[var(--color-accent-oil)]/50 hover:text-[var(--color-accent-oil)] transition-all"
                      >
                        Ver Detalles
                      </Link>
                    </div>
                  </SectionReveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* DIPLOMADOS */}
      <section id="diplomados" className="py-20 bg-[var(--color-bg-primary)]">
        <div className="max-w-6xl mx-auto px-4">
          <SectionReveal>
            <div className="text-center mb-14">
              <div className="flex justify-center mb-4">
                <SectionBadge icon="ri-graduation-cap-line" label="Programas especializados" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Diplomados para profundizar competencias</h2>
              <p className="text-[var(--color-text-secondary)] max-w-2xl mx-auto">
                Formación extendida para profesionales y equipos que requieren una ruta completa de especialización.
              </p>
            </div>
          </SectionReveal>
          {diplomados.length === 0 ? (
            <SectionReveal delay={80}>
              <div className="relative overflow-hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6 md:p-8">
                <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[var(--color-accent-oil)]/10 blur-2xl" />
                <div className="absolute -left-20 bottom-0 w-56 h-56 rounded-full bg-[var(--color-accent)]/10 blur-3xl" />
                <div className="relative grid md:grid-cols-[1.2fr_0.8fr] gap-8 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text-muted)] mb-5">
                      <i className="ri-calendar-check-line text-[var(--color-accent-oil)]"></i>
                      Fechas bajo solicitud
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Diseñamos el diplomado según la necesidad de tu equipo
                    </h3>
                    <p className="text-[var(--color-text-secondary)] leading-relaxed max-w-2xl mb-6">
                      Actualmente no hay fechas públicas abiertas. Podemos estructurar un programa corporativo con módulos, fechas, modalidad y certificación alineados a tus riesgos operativos.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/registro" className="btn-primary inline-flex items-center justify-center gap-2">
                        Solicitar próxima fecha
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                      <a href="#contacto" className="px-5 py-3 rounded-xl border border-[var(--color-border)] text-sm font-semibold text-center hover:border-[var(--color-accent-oil)]/50 hover:text-[var(--color-accent-oil)] transition-all">
                        Hablar con un asesor
                      </a>
                    </div>
                  </div>
                  <div className="grid gap-3">
                    {[
                      ["Diagnóstico", "Levantamos brechas y perfiles del equipo", "ri-search-eye-line"],
                      ["Ruta modular", "Definimos módulos, duración y modalidad", "ri-route-line"],
                      ["Certificación", "Gestionamos evaluación y emisión digital", "ri-award-line"],
                    ].map(([title, desc, icon]) => (
                      <div key={title} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/70 p-4 flex gap-3">
                        <div className="w-10 h-10 rounded-xl border border-[var(--color-accent-oil)]/40 flex items-center justify-center flex-shrink-0">
                          <i className={`${icon} text-[var(--color-accent-oil)]`}></i>
                        </div>
                        <div>
                          <p className="font-semibold text-white">{title}</p>
                          <p className="text-sm text-[var(--color-text-muted)]">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SectionReveal>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {diplomados.map((diplomado, i) => {
                const ed = nextDiplomadoEdition(diplomado);
                return (
                  <SectionReveal key={diplomado.id} delay={i * 60}>
                    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-6 h-full flex flex-col hover:border-[var(--color-accent-oil)]/30 transition-all">
                      <div className="flex items-start justify-between mb-4">
                        {diplomado.image_url ? (
                          <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-3 bg-[var(--color-bg-secondary)]">
                            <img src={courseImageUrl(diplomado.image_url, branding.course_default)} alt={diplomado.name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-11 h-11 rounded-xl border border-[var(--color-accent-oil)]/50 flex items-center justify-center">
                            <i className="ri-graduation-cap-line text-lg text-[var(--color-accent-oil)]"></i>
                          </div>
                        )}
                        {!diplomado.image_url && (
                          <span className="px-3 py-1 rounded-full text-sm font-bold text-[var(--color-accent-oil)] border border-[var(--color-accent-oil)]/40 h-fit">
                            ${(ed?.price || 0).toLocaleString("en-US")}
                          </span>
                        )}
                      </div>
                      {diplomado.image_url && (
                        <div className="flex justify-end mb-2">
                          <span className="px-3 py-1 rounded-full text-sm font-bold text-[var(--color-accent-oil)] border border-[var(--color-accent-oil)]/40 inline-block">
                            ${(ed?.price || 0).toLocaleString("en-US")}
                          </span>
                        </div>
                      )}
                      <h3 className="font-bold text-lg mb-2 line-clamp-2">{diplomado.name}</h3>
                      <p className="text-sm text-[var(--color-text-secondary)] line-clamp-3 flex-grow mb-4">{diplomado.description}</p>
                      {ed && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-text-muted)] mb-5">
                          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{ed.start_date.slice(0, 10)}</span>
                          <span className="flex items-center gap-1"><i className="ri-time-line"></i>{diplomado.hours}h</span>
                          <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{ed.location}</span>
                        </div>
                      )}
                      <Link to={`/diplomado/${diplomado.id}`} className="block w-full text-center py-3 rounded-xl border border-[var(--color-border)] text-sm font-semibold hover:border-[var(--color-accent-oil)]/50 hover:text-[var(--color-accent-oil)] transition-all">
                        Ver Detalles
                      </Link>
                    </div>
                  </SectionReveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* IMPACTO */}
      <section id="impacto" className="py-20 bg-[var(--color-bg-primary)]">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <SectionReveal>
            <div className="mb-4">
              <SectionBadge icon="ri-bar-chart-box-line" label="Impacto" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Resultados que hablan
              <br />
              <span className="text-[var(--color-accent-oil)]">por sí mismos</span>
            </h2>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-10">
              Nuestras capacitaciones han transformado la cultura de seguridad en más de 20 empresas. Reducimos
              incidentes, certificamos competencias y construimos equipos más seguros.
            </p>
            <div className="grid grid-cols-2 gap-8">
              {LANDING_IMPACT.map((item) => (
                <div key={item.label} className="border-l-2 border-[var(--color-accent-oil)] pl-4">
                  <p className="text-3xl md:text-4xl font-bold text-white">{item.value}</p>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </SectionReveal>
          <SectionReveal delay={150}>
            <img
              src={branding.landing_impact || LANDING_IMAGES.impact}
              alt="Trabajadores certificados KSM"
              className="w-full rounded-2xl border border-[var(--color-border)] object-cover aspect-[4/3]"
            />
          </SectionReveal>
        </div>
      </section>

      {/* ÁREAS DE ESPECIALIZACIÓN */}
      <section id="especializacion" className="py-20 bg-[var(--color-bg-secondary)]">
        <div className="max-w-6xl mx-auto px-4">
          <SectionReveal>
            <div className="text-center mb-14">
              <div className="flex justify-center mb-4">
                <SectionBadge icon="ri-compass-3-line" label="Áreas de especialización" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Cubrimos todas las disciplinas clave</h2>
            </div>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LANDING_SPECIALIZATIONS.map((area, i) => (
              <SectionReveal key={area.title} delay={i * 60}>
                <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-6 h-full hover:border-[var(--color-accent-oil)]/30 transition-all">
                  <div className="w-11 h-11 rounded-xl border border-[var(--color-accent-oil)]/50 flex items-center justify-center mb-4">
                    <i className={`${area.icon} text-lg text-[var(--color-accent-oil)]`}></i>
                  </div>
                  <h3 className="font-bold mb-2">{area.title}</h3>
                  <p className="text-sm text-[var(--color-text-secondary)]">{area.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section id="testimonios" className="py-20 bg-[var(--color-bg-primary)]">
        <div className="max-w-6xl mx-auto px-4">
          <SectionReveal>
            <div className="text-center mb-14">
              <div className="flex justify-center mb-4">
                <SectionBadge icon="ri-chat-quote-line" label="Testimonios" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Lo que dicen nuestros clientes</h2>
            </div>
          </SectionReveal>
          <div className="grid md:grid-cols-3 gap-6">
            {LANDING_TESTIMONIALS.map((t, i) => (
              <SectionReveal key={t.name} delay={i * 80}>
                <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-6 h-full flex flex-col">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-[var(--color-accent-oil)] text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-[var(--color-text-secondary)] italic flex-grow leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                  <div className="border-t border-[var(--color-border)] mt-5 pt-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--color-accent-oil)]/20 flex items-center justify-center text-xs font-bold text-[var(--color-accent-oil)]">
                      {t.initials}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-[var(--color-text-muted)]">{t.role}</p>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTICIAS */}
      <section id="noticias" className="py-20 bg-[var(--color-bg-secondary)]">
        <div className="max-w-6xl mx-auto px-4">
          <SectionReveal>
            <div className="mb-10">
              <SectionBadge icon="ri-newspaper-line" label="Noticias" />
              <h2 className="text-3xl md:text-4xl font-bold mt-4">Últimas novedades</h2>
            </div>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {displayNews.map((item, i) => (
              <SectionReveal key={item.id || item.title} delay={i * 60}>
                <Link to={item.id ? `/noticia/${item.id}` : "#"} className="block h-full">
                  <article className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl overflow-hidden h-full hover:border-[var(--color-accent-oil)]/30 transition-all flex flex-col">
                    <img
                      src={assetUrl(item.image_url) || item.image_url}
                      alt={item.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-4 flex-grow flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs mb-2">
                          <span className="font-semibold text-[var(--color-accent-oil)]">{item.category}</span>
                          <span className="text-[var(--color-text-muted)]">{formatDate(item.published_at)}</span>
                        </div>
                        <h3 className="font-bold text-sm leading-snug">{item.title}</h3>
                      </div>
                      <div className="mt-3 text-xs text-[var(--color-accent-oil)] font-medium">Leer más <i className="ri-arrow-right-s-line"></i></div>
                    </div>
                  </article>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONFÍAN EN NOSOTROS */}
      <section id="clientes" className="py-16 bg-[var(--color-bg-primary)]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <SectionReveal>
            <div className="flex justify-center mb-8">
              <SectionBadge icon="ri-hand-heart-line" label="Confían en nosotros" />
            </div>
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-6">
              {partners.length > 0 ? (
                partners.map((p) => (
                  <div key={p.id} className="flex flex-col items-center justify-center opacity-70 hover:opacity-100 transition-opacity">
                    {p.logo_url ? (
                      <img src={assetUrl(p.logo_url)} alt={p.name} className="h-16 md:h-20 object-contain grayscale hover:grayscale-0 transition-all" />
                    ) : (
                      <span className="text-xl md:text-2xl font-bold text-[var(--color-text-muted)] tracking-wider">
                        {p.name}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                LANDING_CLIENTS.map((name) => (
                  <span key={name} className="text-lg md:text-xl font-semibold text-[var(--color-text-muted)] tracking-wider">
                    {name}
                  </span>
                ))
              )}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* CARRERAS */}
      <section id="carreras" className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={branding.landing_careers || LANDING_IMAGES.careers} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#080f1a]/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <SectionReveal>
            <div className="flex justify-center mb-6">
              <SectionBadge icon="ri-briefcase-line" label="Carreras" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Sé parte de nuestro equipo</h2>
            <p className="text-white/75 mb-8 leading-relaxed">
              Buscamos talento apasionado por la seguridad industrial. Únete a una empresa en crecimiento con proyectos
              desafiantes en el sector Oil &amp; Gas.
            </p>
            <a
              href={`mailto:${contactEmail}?subject=CV%20-%20KSM%20Services`}
              className="inline-flex items-center gap-2 bg-[var(--color-accent-oil)] text-[#080f1a] px-8 py-4 rounded-2xl font-bold hover:brightness-110 transition-all"
            >
              <i className="ri-send-plane-line"></i>
              Enviar CV
            </a>
          </SectionReveal>
        </div>
      </section>

      {/* CONTACTO */}
      <section id="contacto" className="py-20 bg-[var(--color-bg-secondary)]">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <SectionReveal>
            <SectionBadge icon="ri-send-plane-line" label="Contacto" />
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-3">Es momento de capacitarte</h2>
            <p className="text-[var(--color-text-secondary)] mb-8">
              Completa el formulario y te contactaremos en menos de 24 horas.
            </p>
                {contactStatus === "success" ? (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-8 text-center animate-[fadeIn_0.5s_ease-out]">
                    <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">¡Mensaje enviado con éxito!</h3>
                    <p className="text-[var(--color-text-secondary)]">Nuestro equipo se pondrá en contacto contigo muy pronto.</p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/40 space-y-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-accent-oil)]/5 rounded-full blur-3xl pointer-events-none -mr-32 -mt-32"></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Tipo de consulta</label>
                        <select name="tipo" className="input-field" value={contactType} onChange={(e) => setContactType(e.target.value as "b2b" | "b2c")} required>
                          <option value="b2b">Empresa</option>
                          <option value="b2c">Participante Independiente</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Nombre completo</label>
                        <input type="text" name="nombre" className="input-field" placeholder="Tu nombre" required />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Correo electrónico</label>
                        <input type="email" name="email" className="input-field" placeholder="correo@ejemplo.com" required />
                      </div>
                    </div>

                    {contactType === "b2b" ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Empresa</label>
                          <input type="text" name="empresa" className="input-field" placeholder="Nombre de tu empresa" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Cargo</label>
                          <input type="text" name="cargo" className="input-field" placeholder="Ej. Gerente de HSE" />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Teléfono</label>
                          <input type="tel" name="telefono" className="input-field" placeholder="Tu teléfono" required />
                        </div>
                      </div>
                    )}

                    {contactType === "b2c" && (
                      <div>
                        <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Mensaje (opcional)</label>
                        <textarea
                          name="mensaje"
                          className="input-field min-h-[100px] resize-none"
                          placeholder="Cuéntanos en qué curso estás interesado..."
                          maxLength={500}
                        />
                      </div>
                    )}

                    {contactType === "b2b" && (
                      <div>
                        <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Mensaje</label>
                        <textarea
                          name="mensaje"
                          className="input-field min-h-[120px] resize-none"
                          placeholder="Cuéntanos qué necesitas..."
                          required
                          maxLength={500}
                        />
                        <p className="text-xs text-[var(--color-text-muted)] mt-2 text-right">Máximo 500 caracteres</p>
                      </div>
                    )}

                    {contactError && (
                      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl text-sm flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        <p>{contactError}</p>
                      </div>
                    )}
                    
                    <button type="submit" disabled={contactStatus === "submitting"} className="btn-primary w-full mt-4 text-base py-4 shadow-xl cursor-pointer flex justify-center items-center">
                      <i className={contactType === "b2b" ? "ri-send-plane-line mr-2" : "ri-user-add-line mr-2"}></i>
                      {contactType === "b2b" 
                        ? (contactStatus === "submitting" ? "Enviando mensaje..." : "Enviar Mensaje")
                        : "Continuar al Registro"}
                    </button>
                  </form>
                )}
          </SectionReveal>
          <SectionReveal delay={100}>
            <img
              src={branding.landing_contact || LANDING_IMAGES.contact}
              alt="Reunión corporativa"
              className="w-full rounded-2xl border border-[var(--color-border)] object-cover aspect-[4/3] hidden lg:block"
            />
          </SectionReveal>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
